var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { PatientConsentExcludedReason } from "./PatientConsentUIManager";
import { NamedRoutes } from "./NamedRoutes";
const t = getObservableT("patients");
export class PatientConsentManagerViewModel {
    constructor(container) {
        this.saveAndExitDialog = new SimpleDialogModel();
        this.supportDialogOpen = false;
        this.patientContactsUIManager = container.patientContactsUIManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.routingUIManager = container.routingUIManager;
    }
    get studyInfo() {
        return this.patientConsentUIManager.studyInfo;
    }
    get isResolvingResumeTarget() {
        return this.patientConsentUIManager.isResolvingResumeTarget;
    }
    get isPDDAGeneralDataUse() {
        return this.patientConsentUIManager.isPDDAGeneralDataUse;
    }
    get showLogo() {
        return this.isPDDAGeneralDataUse && this.currentRoute !== "ENROLLMENT_REQUEST_DETAILS_POPUP";
    }
    get shouldLogoExitOnClick() {
        return this.showLogo && !this.isPreview;
    }
    get showCloseButton() {
        if (this.isPopup)
            return true;
        if (this.isPDDAGeneralDataUse)
            return false;
        if (this.locatingPDDERecords)
            return false;
        if (this.isPatientLoggedIn)
            return true;
        return false;
    }
    get showBackButton() {
        if (this.isPDDAGeneralDataUse)
            return false;
        if (this.isPopup)
            return false;
        if (this.locatingPDDERecords)
            return false;
        if (this.isFirstRoute)
            return false;
        if (this.patientConsentUIManager.excludedReason === PatientConsentExcludedReason.NOT_RECRUITING)
            return false;
        return true;
    }
    get isPatientLoggedIn() {
        return this.patientConsentUIManager.isPatientLoggedIn;
    }
    get showClassicLoginPage() {
        return this.patientConsentUIManager.showClassicLoginPage;
    }
    get currentRoute() {
        return this.patientConsentUIManager.currentPatientConsentRoute;
    }
    get isPopup() {
        return this.patientConsentUIManager.isPopup;
    }
    get progressBarPercentage() {
        return this.patientConsentUIManager.progressBarPercentage;
    }
    get buttonsEnabled() {
        return !this.patientConsentUIManager.session.isSubmittingConsent;
    }
    get isFirstRoute() {
        return this.patientConsentUIManager.isFirstRoute;
    }
    get isPreview() {
        return this.patientConsentUIManager.isInteractivePreview;
    }
    get isScreenshotMode() {
        return this.patientConsentUIManager.isScreenshotMode;
    }
    get isICFTranslationAvailable() {
        return (!this.isPDDAGeneralDataUse && !this.isPreview && this.patientConsentUIManager.studyICFTranslationAvailableLanguages.length > 1);
    }
    get isBackButtonDisabledInPreview() {
        return this.isPreview && this.isFirstRoute;
    }
    get isEdgeCaseRoute() {
        if (!this.currentRoute)
            return false;
        return ["ENROLLMENT_EXCLUDED"].includes(this.currentRoute);
    }
    get isInterimPage() {
        return this.patientConsentUIManager.isInterimPage;
    }
    get isIAS() {
        return this.patientConsentUIManager.isIAS;
    }
    get showProgressBar() {
        if (this.isPopup)
            return false;
        if (!this.isPatientLoggedIn)
            return false;
        if (this.isEdgeCaseRoute)
            return false;
        if (this.locatingPDDERecords)
            return false;
        if (!!this.isPDDAGeneralDataUse || this.isIAS)
            return false;
        return true;
    }
    get showSaveAndExitButton() {
        if (this.isPDDAGeneralDataUse)
            return false;
        return !this.isEdgeCaseRoute;
    }
    get locatingPDDERecords() {
        return this.patientConsentUIManager.locatingPDDERecords;
    }
    get headerPadding() {
        return this.isPDDAGeneralDataUse ? "40px 32px 24px 32px" : undefined;
    }
    goBack() {
        if (this.isBackButtonDisabledInPreview)
            return;
        this.patientConsentUIManager.goToPreviousPage();
    }
    close() {
        if (this.patientConsentUIManager.hidePopupIfPresent()) {
            return;
        }
        if (this.isPreview)
            return;
        this.patientConsentUIManager.close();
    }
    async showSaveAndExitDialog() {
        if (this.isPreview)
            return;
        this.saveAndExitDialog.show();
        const confirmed = await this.saveAndExitDialog.showAndWait();
        if (confirmed) {
            await this.patientConsentUIManager.trackEvent({
                action: "SAVE_AND_EXIT",
            });
            this.patientConsentUIManager.close();
        }
    }
    showSupportDialog() {
        if (this.isPreview)
            return;
        this.supportDialogOpen = true;
    }
    closeSupportDialog() {
        this.supportDialogOpen = false;
    }
    async logoOnClick() {
        if (!this.shouldLogoExitOnClick)
            return;
        if (!this.isPatientLoggedIn) {
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_PATIENT);
            return;
        }
        this.patientConsentUIManager.close();
    }
}
__decorate([
    observable
], PatientConsentManagerViewModel.prototype, "supportDialogOpen", void 0);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "studyInfo", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isResolvingResumeTarget", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isPDDAGeneralDataUse", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "showLogo", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "shouldLogoExitOnClick", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "showCloseButton", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "showBackButton", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isPatientLoggedIn", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "showClassicLoginPage", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "currentRoute", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isPopup", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "progressBarPercentage", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "buttonsEnabled", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isFirstRoute", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isPreview", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isScreenshotMode", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isICFTranslationAvailable", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isBackButtonDisabledInPreview", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isEdgeCaseRoute", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isInterimPage", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "isIAS", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "showProgressBar", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "showSaveAndExitButton", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "locatingPDDERecords", null);
__decorate([
    computed
], PatientConsentManagerViewModel.prototype, "headerPadding", null);
